import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import Col from 'react-bootstrap/lib/Col';
import Row from 'react-bootstrap/lib/Row';
import _ from 'lodash';

import Order from '../../../records/Order';
import StoredRefundCalculations from '../../../records/refund/StoredRefundCalculations';
import t, {hasMessage} from '../../../lib/I18n';

import Price from '../../general/Price';
import FormattedHTMLMessage from '../../general/FormattedHTMLMessage';
import FeatureFlag from '../../general/FeatureFlag';
import HelpIcon from "../../general/HelpIcon";
import VoucherCodeLineItem from './VoucherCodeLineItem';
import OrderStore from '../../../stores/OrderStore';
import useStoreState from '../../../stores/useStoreState';
import { FulfilmentType } from '../../../constants/AppConstants';

const Totals = ({order, refund, adminFee, storedRefundCalculations}) => {
    const { delivery, charity } = order;
    const appliedVoucher = useStoreState(OrderStore, (store) => store.getAppliedVoucher());

    const ticketsPrice = order.getTripsPrice();
    const ticketsPriceWithoutDiscount = order.getTripsPriceWithoutDiscount();
    const discountSaving = ticketsPriceWithoutDiscount - ticketsPrice;
    const applyVoucher = (price) =>
        appliedVoucher ? Math.max(0, price - appliedVoucher.amount) : price;
    const displayedTotal = applyVoucher(order.getTotalPrice());
    const displayedTotalWithoutDiscount = applyVoucher(order.getTotalPrice() + discountSaving);

    const isAdjustedOrder = order.isAmending() || order.hasRefunds();
    const priceColumns = !isAdjustedOrder && discountSaving > 0 ? 6 : 4;

    const renderOriginalPrice = (price, priceWithoutDiscount) =>
        !isAdjustedOrder && priceWithoutDiscount > price && (
            <Fragment>
                <Price
                    className="order-summary__total__original-price"
                    price={priceWithoutDiscount}
                    srOnly="booking.fares.originalPrice"
                />
                <span className="sr-only">. </span>
            </Fragment>
        );

    const deliveryMessage =
        delivery && delivery.fulfilmentMethod ? `delivery.${delivery.internalName}` : null;

    return (
        <div className="order-summary__total margin-top--sm margin-bottom--sm">
            {deliveryMessage && (
                <Row key="fulfilment" className="text-weight--normal margin-bottom--xs">
                    <Col xs={8}>
                        {hasMessage(deliveryMessage) ? (
                            <FormattedHTMLMessage
                                message={deliveryMessage}
                                trademark={<b>&#8482;</b>}
                            />
                        ) : (
                            delivery.fulfilmentMethod === FulfilmentType.S_TICKET ? delivery.displayName : _.startCase(delivery.displayName)
                        )}
                        <span className="sr-only">. </span>
                    </Col>
                    <Col xs={4} className="text-right price">
                        <Price price={delivery.price} showFree />
                        <span className="sr-only">. </span>
                    </Col>
                </Row>
            )}

            <FeatureFlag name="enableDiscountCode">
                {order.getDiscountCodeValue() && (
                    <Row key="discountCode" className="text-weight--normal margin-bottom--xs">
                        <Col xs={8}>
                            {t('order.summary.discount_code')}
                            <span className="sr-only">. </span>
                        </Col>
                        <Col xs={4} className="text-right price">
                            - <Price price={order.getDiscountCodeValue()} />
                            <span className="sr-only">. </span>
                        </Col>
                    </Row>
                )}
            </FeatureFlag>

            <Row key="tickets" className="text-weight--normal margin-bottom--xs">
                <Col xs={12 - priceColumns}>
                    {t('booking.tickets')}
                    <span className="sr-only">. </span>
                </Col>
                <Col xs={priceColumns} className="text-right price">
                    {renderOriginalPrice(ticketsPrice, ticketsPriceWithoutDiscount)}
                    <Price price={ ticketsPrice } />
                    <span className="sr-only">. </span>
                </Col>
            </Row>

            <FeatureFlag name="enableVoucherCode">
                <VoucherCodeLineItem />
            </FeatureFlag>

            {order.getDiscountCode() && (
                <Row key="discount" className="text-weight--normal margin-bottom--xs">
                    <Col xs={8}>
                        {t('order.summary.discount')} ({order.getDiscountCode()})
                        <span className="sr-only">. </span>
                    </Col>
                    <Col xs={4} className="text-right price">
                        {order.getTotalDiscount() ?
                            <React.Fragment>
                                - <Price price={order.getTotalDiscount()}/>
                                <span className="sr-only">. </span>
                            </React.Fragment>
                            :
                            <HelpIcon
                                icon={'info'}
                                heading={order.getIn(['discount', 'name'])}
                                message={order.getIn(['discount', 'description'])}
                            />
                        }
                    </Col>
                </Row>
            )}

            <FeatureFlag name="enableBookingFee">
                <Row key="bookingFee" className="text-weight--normal margin-bottom--xs">
                    <Col xs={8}>
                        {t('order.summary.booking_fee')}
                        <span className="sr-only">. </span>
                    </Col>
                    <Col xs={4} className="text-right price">
                        <Price price={order.getBookingFeePrice()} showFree />
                        <span className="sr-only">. </span>
                    </Col>
                </Row>
            </FeatureFlag>

            {order.hasRefunds() && refund && (
                <div>
                    <Row key="originalPrice" className="text-weight--normal margin-bottom--xs">
                        <Col xs={8}>
                            {t('order.summary.total_paid')}
                            <span className="sr-only">. </span>
                        </Col>
                        <Col xs={4} className="text-right price">
                            <Price price={order.getOriginalTotalPrice()} />
                            <span className="sr-only">. </span>
                        </Col>
                    </Row>
                    {_.get(storedRefundCalculations, 'tickets') &&
                        _.get(storedRefundCalculations, 'tickets').size > 0 && (
                            <Row className="text-weight--norma margin-bottom--xsl">
                                <Col xs={8}>
                                    {t('account.orders.refund.ticketsValue')}
                                    <span className="sr-only">. </span>
                                </Col>
                                <Col xs={4} className="text-right price">
                                    <Price
                                        price={storedRefundCalculations.tickets.reduce(
                                            (a, e) => a + e.calculation.refundAmount,
                                            0,
                                        )}
                                    />
                                    <span className="sr-only">. </span>
                                </Col>
                            </Row>
                        )}

                        {_.get(storedRefundCalculations, 'tickets') &&
                            _.get(storedRefundCalculations, 'tickets').size > 0 && (
                                <Row className="text-weight--normal margin-bottom--xs">
                                    <Col xs={8}>
                                        Total used
                                        <span className="sr-only">. </span>
                                    </Col>
                                    <Col xs={4} className="text-right price">
                                        <Price
                                            price={storedRefundCalculations.tickets.reduce(
                                                (a, e) => a + e.calculation.totalUsed,
                                                0,
                                            )}
                                        />
                                        <span className="sr-only">. </span>
                                    </Col>
                                </Row>
                            )}

                    <Row key="refund" className="text-weight--normal margin-bottom--xs">
                        <Col xs={8}>{t('order.summary.adminFee')}</Col>
                        <Col xs={4} className="text-right price">
                            <Price price={adminFee} />
                        </Col>
                        <Col xs={8}>
                            {t('order.summary.total_refund')}
                            <span className="sr-only">. </span>
                        </Col>
                        <Col xs={4} className="text-right price">
                            <Price price={refund.getRefundAmount()} />
                            <span className="sr-only">. </span>
                        </Col>
                    </Row>
                </div>
            )}
            <FeatureFlag name="enableCharityRoundUp">
                {charity && charity.donationAmount && (
                    <Fragment>
                        <Row key="charity" className="text-weight--normal margin-bottom--xs">
                            <Col xs={8}>
                                {t('order.summary.charity_donation')}
                                <span className="sr-only">. </span>
                            </Col>
                            <Col xs={4} className="text-right price">
                                <Price price={ charity.donationAmount } />
                                <span className="sr-only">. </span>
                            </Col>
                        </Row>
                    </Fragment>
                )}
            </FeatureFlag>
            {!order.hasRefunds() && (
                <Row key="total" className="text-md text-weight--normal text-primary">
                    <Col xs={12 - priceColumns}>
                        {t('order.summary.total_all')}
                        <span className="sr-only">. </span>
                    </Col>
                    <Col xs={priceColumns} className="text-right price">
                        {renderOriginalPrice(displayedTotal, displayedTotalWithoutDiscount)}
                        <Price price={displayedTotal} />
                        <span className="sr-only">. </span>
                    </Col>
                </Row>
            )}
        </div>
    );
};

Totals.propTypes = {
    order: PropTypes.instanceOf(Order).isRequired,
    refund: PropTypes.instanceOf(Order),
    storedRefundCalculations: PropTypes.instanceOf(StoredRefundCalculations),
    adminFee: PropTypes.number,
};

Totals.defaultProps = {
    adminFee: 0,
};

export default Totals;
